import { Platform } from "react-native";
import * as ExpoImagePicker from "expo-image-picker";
import { isVideo, getExtensionFromType } from "@lib/util/file";
import { resolveError } from "@lib/util/error";
import { notify } from "@lib/util/logger";
import { appVersion } from "@constants/Env";
import { DeviceUtils } from "@lib/util/device";
import {
  AllowImageTypes,
  AllowVideoTypes,
  AllowImageTypeReg,
  AllowVideoTypeReg,
} from "@constants/App";

// 動画検証用の定数
export const VIDEO_VALIDATION = {
  NON_MP4: {
    // 非MP4動画のMIMEタイプ（Data URI用）
    // NOTE: video/quicktimeは除外しています。理由：
    // - iPhoneやmacOSで撮影された動画はQuickTimeコンテナを使用しますが、
    //   H.264/H.265コーデックでMP4互換の場合が多い
    // - ブラウザではコーデックレベルの検証が困難なため、
    //   QuickTimeコンテナのMP4互換ファイルを許可します
    MIME_TYPE_PREFIXES: [
      "data:video/x-msvideo", // AVI
      "data:video/x-ms-wmv", // WMV
      "data:video/x-flv", // FLV
      "data:video/x-matroska", // MKV
      "data:video/webm", // WebM
      "data:video/ogg", // OGG
      "data:video/3gpp", // 3GP
      "data:video/3gpp2", // 3G2
    ],
    // 非MP4動画ファイル拡張子
    // NOTE: .movはQuickTimeコンテナ（video/quicktime）として許可するため除外
    VIDEO_EXTENSIONS: /\.(avi|wmv|flv|mkv|webm|ogv|m4v|3gp|3g2)$/i,
  },
  ERROR_MESSAGE:
    "この動画形式はサポートされていません。標準的なMP4形式（H.264/H.265コーデック）に変換してください",
} as const;

// ファイルが動画（MP4以外）かどうかを検出するヘルパー関数
// 戦略：明確に非MP4と判別できた場合のみ拒否（寛容なブラックリスト方式）
export const isNonMp4Video = (
  asset: ExpoImagePicker.ImagePickerAsset
): boolean => {
  const { uri, fileName } = asset;
  const uriLower = uri.toLowerCase();

  // 1. 非MP4のファイル拡張子を持つ場合は明確に拒否
  if (
    fileName != null &&
    VIDEO_VALIDATION.NON_MP4.VIDEO_EXTENSIONS.test(fileName)
  ) {
    return true;
  }

  // 2. Data URIの場合、非MP4のMIMEタイプをチェック
  if (uriLower.startsWith("data:video/")) {
    const hasNonMp4MimeType = VIDEO_VALIDATION.NON_MP4.MIME_TYPE_PREFIXES.some(
      (mimeTypePrefix) => uriLower.startsWith(mimeTypePrefix)
    );
    if (hasNonMp4MimeType) {
      return true;
    }
  }

  // 上記のいずれにも該当しない場合は許可（QuickTime/MP4互換ファイルを含む）
  return false;
};

// Webプラットフォームで動画ファイルを検証するヘルパー関数
export const validateVideoFileOnWeb = (
  asset: ExpoImagePicker.ImagePickerAsset
): void => {
  if (Platform.OS === "web" && isNonMp4Video(asset)) {
    try {
      // 詳細なSlack通知（失敗しても検証処理は継続）
      const errorDetails = collectErrorDetails(
        new Error(VIDEO_VALIDATION.ERROR_MESSAGE),
        {
          operation: "VideoValidation",
          assetInfo: {
            uri: asset.uri,
            fileName: asset.fileName,
            fileSize: asset.fileSize,
            width: asset.width,
            height: asset.height,
            type: asset.type,
          },
          additionalInfo: {
            detectedAsNonMp4Video: true,
            platform: "web",
          },
        }
      );

      safeNotify({
        type: "ImagePickerError: VideoValidation",
        searchableType: "iOS_IMAGE_UPLOAD_ERROR_VideoValidation",
        ...errorDetails,
      });
    } catch (notificationError) {
      // Slack通知が失敗しても検証エラーは継続
      // eslint-disable-next-line no-console
      console.warn("Video validation notification failed:", notificationError);
    }

    throw new Error(VIDEO_VALIDATION.ERROR_MESSAGE);
  }
};

// エラー詳細情報を収集するヘルパー関数（安全に保護）
export const collectErrorDetails = (
  error: unknown,
  context: {
    operation: string;
    assetInfo?: Partial<ExpoImagePicker.ImagePickerAsset>;
    additionalInfo?: Record<string, unknown>;
  }
) => {
  try {
    const errorInfo = resolveError(error);
    return {
      error: {
        message: errorInfo.message,
        code: errorInfo.code,
        name: error instanceof Error ? error.name : "Error",
        stack: error instanceof Error ? error.stack : undefined,
      },
      context: {
        operation: context.operation,
        platform: Platform.OS,
        appVersion,
        deviceInfo: {
          brand: DeviceUtils.brand,
          modelName: DeviceUtils.modelName,
          osName: DeviceUtils.osName,
          osVersion: DeviceUtils.osVersion,
        },
        assetInfo:
          context.assetInfo !== undefined
            ? {
                uri:
                  context.assetInfo.uri !== undefined &&
                  context.assetInfo.uri !== ""
                    ? `${context.assetInfo.uri.substring(0, 50)}...`
                    : undefined,
                fileName: context.assetInfo.fileName,
                fileSize: context.assetInfo.fileSize,
                width: context.assetInfo.width,
                height: context.assetInfo.height,
                type: context.assetInfo.type,
              }
            : undefined,
        additionalInfo: context.additionalInfo,
      },
      // バグ調査強化メタデータ
      investigation: {
        relatedIssues: [
          "iOS画像アップロード失敗",
          "サービスダウン誤表示",
          "FormDataエラー",
        ],
        troubleshootingSteps: [
          "1. ファイルURIの有効性確認",
          "2. React Native FormData処理確認",
          "3. GraphQL mutation実行状況確認",
          "4. RelayEnvironment.getFile処理確認",
        ],
        expectedBehavior: "画像選択後、正常にサーバーにアップロードされる",
        actualBehavior: "サービスがダウンしていますエラーが表示される",
        impactLevel: "HIGH", // iOS環境でのアップロード機能完全停止
        frequency: "INTERMITTENT", // 間欠的に発生
      },
      timestamp: new Date().toISOString(),
    };
  } catch (detailError) {
    // エラー詳細収集が失敗しても、最低限の情報を返す
    return {
      error: {
        message: typeof error === "string" ? error : "Unknown error",
        code: "DETAIL_COLLECTION_FAILED",
      },
      context: {
        operation: context.operation,
        platform: Platform.OS,
      },
      timestamp: new Date().toISOString(),
    };
  }
};

// バグ調査用の統一ログIDを生成
export const generateBugTrackingId = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  return `iOS_IMAGE_UPLOAD_BUG_${timestamp}_${random}`;
};

// Slack通知を安全に実行するヘルパー関数（バグ調査最適化）
export const safeNotify = (
  data: unknown,
  options?: { level?: "info" | "warning" | "error" }
) => {
  try {
    const bugTrackingId = generateBugTrackingId();
    const level = options?.level ?? "warning";
    const enhancedData = {
      ...(typeof data === "object" && data !== null ? data : {}),
      // バグ調査用のメタデータ
      bugTracking: {
        id: bugTrackingId,
        component: "ImagePicker",
        issue: "iOS_IMAGE_UPLOAD_FAILURE",
        keywords: [
          "iOS",
          "画像アップロード",
          "サービスダウンエラー",
          "FormData",
          "React Native",
          "ImagePicker",
        ],
        version: appVersion,
        timestamp: new Date().toISOString(),
      },
    };

    notify(enhancedData, { level });

    // コンソールにも統一IDでログ出力（検索用）
    // eslint-disable-next-line no-console
    console.log(`[${bugTrackingId}] Bug tracking notification sent (${level})`);
  } catch (notifyError) {
    // notify が失敗しても既存動作を妨げない
    // eslint-disable-next-line no-console
    console.warn("Slack notification failed:", notifyError);
  }
};

// React Native環境でファイルオブジェクトを作成（フォールバック機構付き）
export const createRNFileWithFallback = async (
  uri: string,
  index: number = 0
): Promise<{ uri: string; file: File }> => {
  const isVideoFile = isVideo({ uri });
  const timestamp = Date.now();
  const fileName = isVideoFile
    ? `video_${timestamp}_${index}.mp4`
    : `image_${timestamp}_${index}.jpg`;
  const mimeType = isVideoFile ? "video/mp4" : "image/jpeg";

  // まず新しい軽量オブジェクト方式を試行
  try {
    // eslint-disable-next-line no-console
    console.log(`Attempting lightweight object creation for ${uri}`);

    // URIの基本検証
    if (uri === undefined || uri === "" || !uri.startsWith("file://")) {
      // eslint-disable-next-line no-console
      console.warn(`URI validation warning: ${uri}, attempting fallback`);
      throw new Error("Invalid URI detected, triggering fallback");
    }

    const file = {
      uri,
      name: fileName,
      type: mimeType,
    } as unknown as File;

    // eslint-disable-next-line no-console
    console.log(`Lightweight object created successfully:`, {
      name: fileName,
      type: mimeType,
      uriLength: uri.length,
    });

    return { uri, file };
  } catch (lightweightError) {
    // 軽量オブジェクト作成が失敗した場合、元のBlob/File生成方式にフォールバック
    // eslint-disable-next-line no-console
    console.warn(
      `Lightweight object creation failed, falling back to Blob/File generation:`,
      lightweightError
    );

    try {
      // 元の実装: Blobを作成してFile型を生成
      // eslint-disable-next-line no-console
      console.log(`Attempting Blob/File fallback for ${uri}`);

      const blob = await fetch(uri).then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.statusText}`);
        }
        return response.blob();
      });

      const extension = getExtensionFromType(blob.type);
      const fallbackFileName = `${timestamp}_fallback_${index}.${extension}`;
      const file = new File([blob], uri, { type: blob.type });

      // eslint-disable-next-line no-console
      console.log(`Blob/File fallback successful:`, {
        name: fallbackFileName,
        type: file.type,
        size: file.size,
      });

      // Slack通知（フォールバック成功をログ）
      safeNotify({
        type: "ImagePickerInfo: FallbackSuccess",
        searchableType: "iOS_IMAGE_UPLOAD_FALLBACK_SUCCESS",
        message: "Successfully fell back to original Blob/File generation",
        context: {
          uri: `${uri.substring(0, 50)}...`,
          fileName: fallbackFileName,
          fileType: file.type,
          fileSize: file.size,
        },
      });

      return { uri, file };
    } catch (fallbackError) {
      // 両方の方法が失敗した場合、最終手段として元のFile型のような構造を返す
      // eslint-disable-next-line no-console
      console.error(
        `Both lightweight and Blob/File methods failed, using final fallback:`,
        fallbackError
      );

      // Slack通知（完全フォールバック）
      safeNotify({
        type: "ImagePickerWarning: CompleteFallback",
        searchableType: "iOS_IMAGE_UPLOAD_COMPLETE_FALLBACK",
        message: "Using final fallback after both methods failed",
        error: resolveError(fallbackError).message,
        context: {
          uri: `${uri.substring(0, 50)}...`,
          fileName,
          mimeType,
        },
      });

      // 最終フォールバック: 元の実装と同じ形式
      const finalFile = new File([new Blob([])], uri, { type: mimeType });

      return { uri, file: finalFile };
    }
  }
};

// data URIをBlobに変換するヘルパー関数（iOS Safari fetch制限の回避用）
// Safari/WebKitはfetch()でdata URIを読み込むことをサポートしていないため、
// 同期的な変換（atob + Uint8Array）を使用
export const dataURItoBlob = (dataURI: string): Blob => {
  try {
    // data:[<mime>][;base64],<data> のフォーマットをパース
    const parts = dataURI.split(",");
    if (parts.length < 2) {
      throw new Error("Invalid data URI format");
    }

    // MIMEタイプを抽出（例: "data:video/mp4;base64" -> "video/mp4"）
    const mimeMatch = parts[0].match(/:(.*?)(;|$)/);
    const mime =
      mimeMatch?.[1] !== undefined && mimeMatch[1] !== ""
        ? mimeMatch[1]
        : "application/octet-stream";
    const base64Data = parts[1];

    // base64デコード
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);

    for (let i = 0; i < binaryString.length; i += 1) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return new Blob([bytes], { type: mime });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Failed to convert data URI to Blob:", error);
    throw new Error(
      `data URI変換に失敗しました: ${resolveError(error).message}`
    );
  }
};

// Blob URLからBlobを取得するヘルパー関数（fetch失敗時のXMLHttpRequest fallback付き）
// 大きなファイル（146MB動画など）でfetch()が失敗する場合に、より安定したXMLHttpRequestを使用
export const fetchBlobWithFallback = async (
  blobUrl: string,
  timeoutMs: number = 120000
): Promise<Blob> => {
  // まずfetch()を試行（標準的な方法）
  try {
    // eslint-disable-next-line no-console
    console.log(
      `Attempting fetch() for blob URL: ${blobUrl.substring(0, 50)}...`
    );

    const response = await fetch(blobUrl);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const blob = await response.blob();

    // eslint-disable-next-line no-console
    console.log(
      `fetch() succeeded, blob size: ${blob.size} bytes, type: ${blob.type}`
    );

    return blob;
  } catch (fetchError) {
    // fetch()が失敗した場合、XMLHttpRequestにフォールバック
    const errorInfo = resolveError(fetchError);
    // eslint-disable-next-line no-console
    console.warn(
      `fetch() failed for ${blobUrl.substring(0, 50)}...: ${
        errorInfo.message
      } (code: ${errorInfo.code}), attempting XMLHttpRequest fallback`
    );

    return new Promise<Blob>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("GET", blobUrl, true);
      xhr.responseType = "blob";
      xhr.timeout = timeoutMs;

      xhr.onload = () => {
        // blob URLの場合はstatusが0、HTTPの場合は200-299が成功
        const isSuccess =
          (xhr.status === 0 || (xhr.status >= 200 && xhr.status < 300)) &&
          xhr.response instanceof Blob;

        if (isSuccess) {
          // eslint-disable-next-line no-console
          console.log(
            `XMLHttpRequest fallback succeeded, blob size: ${xhr.response.size} bytes, type: ${xhr.response.type}, status: ${xhr.status}`
          );

          // Slack通知（fallback成功）
          safeNotify({
            type: "ImagePickerInfo: XMLHttpRequestFallbackSuccess",
            searchableType: "iOS_IMAGE_UPLOAD_XHR_FALLBACK_SUCCESS",
            message: "fetch() failed but XMLHttpRequest fallback succeeded",
            context: {
              blobUrl: `${blobUrl.substring(0, 50)}...`,
              blobUrlLength: blobUrl.length,
              blobSize: xhr.response.size,
              blobType: xhr.response.type,
              xhrStatus: xhr.status,
              xhrStatusText: xhr.statusText,
              fetchError: resolveError(fetchError).message,
              timeoutMs,
            },
          });

          resolve(xhr.response);
        } else {
          const errorMessage = `XHR onload failed: status ${xhr.status} ${
            xhr.statusText
          }, response type: ${typeof xhr.response}`;
          // eslint-disable-next-line no-console
          console.error(errorMessage);

          // Slack通知（XHR onload失敗）
          safeNotify({
            type: "ImagePickerError: XMLHttpRequestOnloadFailed",
            searchableType: "iOS_IMAGE_UPLOAD_XHR_ONLOAD_FAILED",
            message: "XMLHttpRequest onload completed but validation failed",
            context: {
              blobUrl: `${blobUrl.substring(0, 50)}...`,
              blobUrlLength: blobUrl.length,
              xhrStatus: xhr.status,
              xhrStatusText: xhr.statusText,
              xhrResponseType: typeof xhr.response,
              xhrResponseIsBlob: xhr.response instanceof Blob,
              fetchError: resolveError(fetchError).message,
              timeoutMs,
            },
          });

          reject(new Error(errorMessage));
        }
      };

      xhr.onerror = () => {
        const errorMessage = "XHR request failed (network error)";
        // eslint-disable-next-line no-console
        console.error(errorMessage);

        // Slack通知（XHRネットワークエラー）
        safeNotify({
          type: "ImagePickerError: XMLHttpRequestNetworkError",
          searchableType: "iOS_IMAGE_UPLOAD_XHR_NETWORK_ERROR",
          message: "XMLHttpRequest failed with network error",
          context: {
            blobUrl: `${blobUrl.substring(0, 50)}...`,
            blobUrlLength: blobUrl.length,
            xhrStatus: xhr.status,
            xhrStatusText: xhr.statusText,
            fetchError: resolveError(fetchError).message,
            timeoutMs,
          },
        });

        reject(new Error(errorMessage));
      };

      xhr.ontimeout = () => {
        const errorMessage = `XHR request timed out after ${timeoutMs}ms`;
        // eslint-disable-next-line no-console
        console.error(errorMessage);

        // Slack通知（XHRタイムアウト）
        safeNotify({
          type: "ImagePickerError: XMLHttpRequestTimeout",
          searchableType: "iOS_IMAGE_UPLOAD_XHR_TIMEOUT",
          message: `XMLHttpRequest timed out after ${timeoutMs}ms`,
          context: {
            blobUrl: `${blobUrl.substring(0, 50)}...`,
            blobUrlLength: blobUrl.length,
            xhrStatus: xhr.status,
            xhrStatusText: xhr.statusText,
            fetchError: resolveError(fetchError).message,
            timeoutMs,
          },
        });

        reject(new Error(errorMessage));
      };

      // eslint-disable-next-line no-console
      console.log(
        `Starting XMLHttpRequest for blob URL with ${timeoutMs}ms timeout`
      );

      xhr.send();
    });
  }
};

// ファイルサイズをMBに変換するヘルパー関数
export function convertToMB(fileSize?: number): number | null {
  if (fileSize !== undefined) {
    return Math.floor((fileSize / (1000 * 1000)) * 10) / 10;
  }
  return null;
}

// サポートされているMIMEタイプかどうかを判定するヘルパー関数
export function isSupportedMimeType(type: string): boolean {
  return AllowImageTypeReg.test(type) || AllowVideoTypeReg.test(type);
}

// ファイル拡張子からサポートされているファイルかどうかを判定するヘルパー関数
// ブラウザがMIMEタイプを正しく返さない場合のフォールバック用
const SUPPORTED_IMAGE_EXTENSIONS = /\.(jpe?g|png|gif)$/i;
const SUPPORTED_VIDEO_EXTENSIONS = /\.(mp4|mov)$/i;

export function isSupportedFileExtension(fileName: string): boolean {
  return (
    SUPPORTED_IMAGE_EXTENSIONS.test(fileName) ||
    SUPPORTED_VIDEO_EXTENSIONS.test(fileName)
  );
}

// MIMEタイプまたはファイル拡張子でサポートされているファイルかどうかを判定するヘルパー関数
// MIMEタイプが空または不明な場合にファイル拡張子でフォールバック
export function isSupportedFile(type: string, fileName: string): boolean {
  // MIMEタイプが有効な場合はMIMEタイプで判定
  if (type !== "" && type !== "application/octet-stream") {
    return isSupportedMimeType(type);
  }
  // MIMEタイプが空または不明な場合はファイル拡張子で判定
  return isSupportedFileExtension(fileName);
}

// メディアタイプモードに応じてサポートされているMIMEタイプのリストを返すヘルパー関数
export function supportMimeTypes(
  mode: ExpoImagePicker.MediaTypeOptions
): string[] {
  switch (mode) {
    case ExpoImagePicker.MediaTypeOptions.Images:
      return AllowImageTypes;
    case ExpoImagePicker.MediaTypeOptions.Videos:
      return AllowVideoTypes;
    case ExpoImagePicker.MediaTypeOptions.All:
    default:
      return AllowImageTypes.concat(AllowVideoTypes);
  }
}
